import { Check, MapPin, Store, Truck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import { calculateDeliveryCost } from '../services/pricingService';
import { useBooking } from '../contexts/BookingContext';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/delivery-options-light.css';
import MapaEntrega from './MapaEntrega';
import PriceBreakdown from './PriceBreakdown';
import StepContainer from './StepContainer';

const DeliveryOptions = ({ onSelectOption, initialDeliveryOption }) => {
  const { updatePricingWithSeasons, bookingData } = useBooking();
  const { t } = useTranslation();
  // Ref para rastrear cuando la actualización viene del usuario (desde el mapa)
  const isUserAddressUpdate = useRef(false);
  // Ref para mantener la dirección más reciente actualizada por el usuario
  const latestAddressRef = useRef('');
  // Inicializar con valores del contexto si están disponibles, sino con initialDeliveryOption
  const addressFromContext = bookingData?.deliveryOption?.address;
  const typeFromContext = bookingData?.deliveryOption?.type;
  const [deliveryType, setDeliveryType] = useState(
    typeFromContext || initialDeliveryOption?.type || ''
  );
  const [address, setAddress] = useState(
    addressFromContext || initialDeliveryOption?.address || ''
  );
  
  // Inicializar el ref con la dirección inicial cuando el componente se monta
  useEffect(() => {
    const initialAddressValue = addressFromContext || initialDeliveryOption?.address || '';
    if (initialAddressValue && latestAddressRef.current === '') {
      latestAddressRef.current = initialAddressValue;
    }
  }, []); // Solo ejecutar una vez al montar
  // Usar el coste del contexto si está disponible, sino el de initialDeliveryOption
  // Si es delivery y no hay coste, establecer mínimo de 40€
  const costeInicial = bookingData?.pricing?.delivery?.cost || initialDeliveryOption?.cost;
  const [deliveryCost, setDeliveryCost] = useState(
    initialDeliveryOption?.type === 'delivery' && (!costeInicial || costeInicial === 0)
      ? 40
      : (costeInicial || null)
  );

  // Sincronizar solo la dirección desde el contexto cuando aplique. No sincronizar el tipo de entrega
  // desde aquí: se establece solo al montar y al hacer clic en una opción; si lo sincronizáramos,
  // el contexto podría llegar con retraso y sobrescribir la opción recién elegida (flickering).
  useEffect(() => {
    if (isUserAddressUpdate.current) {
      const timer = setTimeout(() => {
        isUserAddressUpdate.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }

    const deliveryOptionFromContext = bookingData?.deliveryOption;
    const deliveryOptionToUse = deliveryOptionFromContext || initialDeliveryOption;

    if (!deliveryOptionToUse) return;

    const newType = deliveryOptionToUse.type || '';
    const newAddress = deliveryOptionToUse.address || '';

    // Solo sincronizar dirección cuando el tipo del contexto coincide con el actual. Si el usuario
    // acaba de pasar a "recogida en tienda", el contexto puede seguir con type 'delivery' un momento;
    // no restaurar la dirección antigua en ese caso.
    if (newType !== deliveryType) return;

    setAddress(prevAddress => {
      const prevAddressTrimmed = (prevAddress || '').trim();
      const newAddressTrimmed = (newAddress || '').trim();
      if (prevAddressTrimmed === newAddressTrimmed) return prevAddress;
      if (newAddressTrimmed) return newAddress;
      if (!newAddressTrimmed && prevAddressTrimmed && deliveryType === 'delivery') {
        if (deliveryOptionFromContext && !deliveryOptionFromContext.address) return prevAddress;
        return prevAddress;
      }
      return newAddress;
    });
  }, [initialDeliveryOption, bookingData?.deliveryOption, deliveryType]);


  // Sincronizar el coste desde el contexto cuando esté disponible
  useEffect(() => {
    if (deliveryType === 'delivery') {
      const costeDelContexto = bookingData?.pricing?.delivery?.cost;
      if (costeDelContexto !== undefined && costeDelContexto !== null) {
        // Asegurar que el coste mínimo sea 40€
        const costeFinal = costeDelContexto > 0 ? costeDelContexto : 40;
        setDeliveryCost(costeFinal);
      } else if (!address) {
        // Si no hay coste en el contexto y no hay dirección, establecer mínimo de 40€
        setDeliveryCost(40);
      }
    } else if (deliveryType === 'pickup') {
      // Cuando es pickup, el coste debe ser 0
      setDeliveryCost(0);
    }
  }, [bookingData?.pricing?.delivery?.cost, deliveryType, address]);

  // Manejar cambio de dirección desde el mapa
  const handleAddressFromMap = useCallback((newAddress) => {
    if (newAddress && typeof newAddress === 'string' && newAddress.trim()) {
      const trimmedAddress = newAddress.trim();
      
      // Marcar que esta actualización viene del usuario para evitar que el useEffect interfiera
      isUserAddressUpdate.current = true;
      
      // Guardar la dirección más reciente en el ref
      latestAddressRef.current = trimmedAddress;
      
      // Actualizar el estado local directamente para que el textarea se actualice inmediatamente
      setAddress(trimmedAddress);
      
      // Actualizar también el contexto de reserva, manteniendo distancia y coste si existen
      if (deliveryType === 'delivery') {
        const distanciaGuardada = bookingData?.deliveryOption?.distance || null;
        const updatedDeliveryOption = {
          type: 'delivery',
          address: trimmedAddress,
          cost: deliveryCost || 40,
          distance: distanciaGuardada
        };
        updatePricingWithSeasons({
          deliveryOption: updatedDeliveryOption
        });
      }
    } else {
      // Solo limpiar si realmente no hay dirección y estamos en modo delivery
      // No limpiar si el usuario acaba de seleccionar una dirección
      // No limpiar automáticamente para evitar que se pierda la dirección seleccionada
    }
  }, [deliveryType, deliveryCost, bookingData?.deliveryOption?.distance, updatePricingWithSeasons]);

  // Manejar cambio de distancia desde el mapa
  const handleDistanceFromMap = useCallback((distance) => {
    // Guardar la distancia en el contexto junto con la dirección y coste
    // Calcular el coste basado en la distancia
    if (deliveryType === 'delivery' && distance !== null) {
      let costeCalculado = 40; // Coste base
      if (distance > 15) {
        const kmExtra = distance - 15;
        const adicional = kmExtra * 2.5;
        costeCalculado = 40 + parseFloat(adicional.toFixed(2));
      }
      
      // Actualizar el coste local
      setDeliveryCost(costeCalculado);
      
      // Usar la dirección más reciente del ref (actualizada por handleAddressFromMap)
      // o del contexto como fallback
      const direccionActual = latestAddressRef.current || bookingData?.deliveryOption?.address || address;
      
      const updatedDeliveryOption = {
        type: 'delivery',
        address: direccionActual,
        cost: costeCalculado,
        distance: distance
      };
      updatePricingWithSeasons({
        deliveryOption: updatedDeliveryOption
      });
    }
  }, [deliveryType, address, bookingData?.deliveryOption?.address, updatePricingWithSeasons]);

  // Manejar cambio de coste de entrega desde el mapa
  const handleDeliveryCostFromMap = useCallback(async (cost, distance = null) => {
    if (cost !== null && deliveryType === 'delivery') {
      setDeliveryCost(cost);
      
      // Usar la distancia pasada como parámetro o la guardada del contexto
      const distanciaFinal = distance !== null ? distance : (bookingData?.deliveryOption?.distance || null);
      
      // Usar la dirección más reciente del ref (actualizada por handleAddressFromMap)
      // o del contexto como fallback
      const direccionActual = latestAddressRef.current || bookingData?.deliveryOption?.address || address;
      
      // Actualizar el contexto con el nuevo coste de entrega, distancia y dirección
      const updatedDeliveryOption = {
        type: 'delivery',
        address: direccionActual,
        cost: cost,
        distance: distanciaFinal
      };
      
      // Actualizar el pricing en el contexto para que se refleje en todos los pasos
      await updatePricingWithSeasons({
        deliveryOption: updatedDeliveryOption
      });
    }
  }, [deliveryType, address, bookingData?.deliveryOption?.address, bookingData?.deliveryOption?.distance, updatePricingWithSeasons]);

  // Construir accessoriesByModel desde los datos disponibles
  const accessoriesByModel = useMemo(() => {
    const result = {};
    const selectedVehicles = bookingData?.selectedVehicles || [];
    const selectedAccessories = bookingData?.selectedAccessories || {};
    const accessories = bookingData?.accessories || [];

    // Agrupar accesorios por modelId
    selectedVehicles.forEach(vehicle => {
      if (vehicle.modelId) {
        // Obtener los IDs de accesorios seleccionados para este vehículo
        const vehicleAccessoryIds = selectedAccessories[vehicle.id] || [];
        
        // Filtrar los accesorios que pertenecen a este vehículo
        const modelAccessories = accessories.filter(acc => 
          vehicleAccessoryIds.includes(acc.id)
        );
        
        // Si ya hay accesorios para este modelo, combinarlos, sino crear nuevo array
        if (result[vehicle.modelId]) {
          // Evitar duplicados
          const existingIds = result[vehicle.modelId].map(a => a.id);
          modelAccessories.forEach(acc => {
            if (!existingIds.includes(acc.id)) {
              result[vehicle.modelId].push(acc);
            }
          });
        } else {
          result[vehicle.modelId] = [...modelAccessories];
        }
      }
    });

    return result;
  }, [bookingData?.selectedVehicles, bookingData?.selectedAccessories, bookingData?.accessories]);

  const handleSubmit = () => {
    if (deliveryType) {
      // Asegurar que el coste mínimo para delivery sea 40€
      const costeFinal = deliveryType === 'delivery' 
        ? (deliveryCost && deliveryCost > 0 ? deliveryCost : 40)
        : 0;
      
      onSelectOption({
        type: deliveryType,
        address: deliveryType === 'delivery' ? address : null,
        cost: costeFinal
      });
    }
  };

  const deliveryOptions = [
    {
      id: 'pickup',
      title: t('delivery.pickup.title'),
      description: t('delivery.pickup.description'),
      icon: Store,
      cost: 0,
      benefits: [
        t('delivery.pickup.benefit.no.cost'),
        t('delivery.pickup.benefit.immediate'),
        t('delivery.pickup.benefit.assistance'),
        t('delivery.pickup.benefit.review')
      ]
    },
    {
      id: 'delivery',
      title: t('delivery.delivery.title'),
      description: t('delivery.delivery.description'),
      icon: Truck,
      cost: 40,
      benefits: [
        t('delivery.delivery.benefit.location'),
        t('delivery.delivery.benefit.flexible'),
        t('delivery.delivery.benefit.assembly'),
        t('delivery.delivery.benefit.pickup'),
        t('delivery.delivery.benefit.fixed.cost')
      ]
    }
  ];

  return (
    <div className="delivery-options-light">
      <div className="max-w-7xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-2c3e50 font-orbitron mb-2">{t('delivery.title')}</h2>
      </div>

      <div className="mb-6">
        <p className="text-6c757d text-center">
          {t('delivery.subtitle')}
        </p>
      </div>

      {/* Resumen de precios - Mostrar arriba en móviles */}
      <div className="lg:hidden mb-6">
        <PriceBreakdown 
          selectedVehicles={bookingData?.selectedVehicles || []}
          selectedDates={bookingData?.dates || {}}
          deliveryType={deliveryType || 'pickup'}
          selectedAccessories={bookingData?.selectedAccessories || {}}
          accessoriesByModel={accessoriesByModel}
          pricing={bookingData?.pricing}
          showInsuranceWarnings
        />
      </div>

      {/* Layout principal */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Contenido principal */}
        <div className="flex-1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {deliveryOptions.map((option) => {
          const IconComponent = option.icon;
          const isSelected = deliveryType === option.id;
          
          return (
            <div
              key={option.id}
              className={`bg-background border rounded-xl cursor-pointer transition-all duration-300 hover:shadow-lg p-6 ${
                isSelected 
                  ? 'selected' 
                  : 'border-gray-custom hover:border-primary'
              }`}
              onClick={async () => {
                const newType = option.id;

                // Si se cambia a pickup, resetear el coste y actualizar pricing
                if (newType === 'pickup') {
                  setDeliveryType('pickup');
                  setDeliveryCost(0);
                  setAddress('');
                  // Actualizar el contexto inmediatamente
                  await updatePricingWithSeasons({
                    deliveryOption: { type: 'pickup', address: null, cost: 0 }
                  });
                } else if (newType === 'delivery') {
                  setDeliveryType('delivery');
                  // Si se cambia a delivery, establecer coste mínimo de 40€ si no hay dirección
                  if (!address || deliveryCost === null || deliveryCost === 0) {
                    setDeliveryCost(40);
                    // Actualizar el contexto con el coste mínimo
                    updatePricingWithSeasons({
                      deliveryOption: { type: 'delivery', address: address || null, cost: 40 }
                    });
                  }
                }
              }}
            >
              <div className="flex items-center mb-4">
                <div className={`p-3 rounded-full mr-4 ${
                  isSelected ? 'bg-EBC122 text-2c3e50' : 'bg-6c757d text-white'
                }`}>
                  <IconComponent size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold mb-1 text-2c3e50">
                      {option.id === 'delivery' ? t('delivery.delivery.title.from.price') : option.title}
                    </h3>
                    {option.cost > 0 && option.id !== 'delivery' ? (
                      <span className="text-lg font-bold text-EBC122">
                        +{option.cost}€
                      </span>
                    ) : null}
                  </div>
                  <p className="text-6c757d text-sm">{option.description}</p>
                </div>
                {isSelected && (
                  <div className="bg-EBC122 text-2c3e50 p-1 rounded-full">
                    <Check size={16} />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {option.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <div className={`w-2 h-2 rounded-full mr-3 ${
                      isSelected ? 'bg-EBC122' : 'bg-6c757d'
                    }`}></div>
                    <span className={isSelected ? 'text-2c3e50' : 'text-6c757d'}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {deliveryType === 'delivery' && (
        <>
        <MapaEntrega 
          onAddressChange={handleAddressFromMap}
          onDistanceChange={handleDistanceFromMap}
          onDeliveryCostChange={handleDeliveryCostFromMap}
          initialAddress={bookingData?.deliveryOption?.address || address || ''}
          initialDistance={bookingData?.deliveryOption?.distance || null}
        />
          {(() => {
            // Usar el coste del contexto si está disponible, sino el del estado local
            const costeAMostrar = bookingData?.pricing?.delivery?.cost !== undefined 
              ? bookingData.pricing.delivery.cost 
              : deliveryCost;
            
            return costeAMostrar !== null && costeAMostrar !== undefined ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-green-800 mb-1">
                      {t('delivery.cost.calculated')}
                    </p>
                    <p className="text-xs text-green-700">
                      {t('delivery.cost.based.on.distance')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-800">
                      {costeAMostrar.toFixed(2)} €
                    </p>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-2c3e50 mb-4">{t('delivery.info.title')}</h3>
            <div className="space-y-3 text-sm text-2c3e50">
              <p>
                {t('delivery.info.base.cost')}
              </p>
              <p>
                {t('delivery.info.mallorca.only')}
              </p>
              <p>
                {t('delivery.info.supplement')}
              </p>
              <p>
                {t('delivery.info.distance.calculation')}
              </p>
              <div className="bg-white border border-blue-200 rounded-lg p-4 mt-4">
                <p className="font-semibold mb-2 text-2c3e50">{t('delivery.info.example')}</p>
                <p className="text-6c757d">
                  {t('delivery.info.example.text')}
                </p>
              </div>
              <p className="mt-4 text-6c757d italic">
                {t('delivery.info.transparent')}
              </p>
            </div>
          </div>
          <div className="bg-background border border-primary rounded-xl p-6 mb-6">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-primary/10 text-primary rounded-full mr-3">
                <MapPin size={20} />
              </div>
              <h3 className="text-lg font-semibold text-2c3e50">{t('delivery.address.title')}</h3>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-2c3e50">{t('delivery.address.full')}</label>
              <textarea
                  className="w-full bg-white border border-gray-300 text-2c3e50 rounded-lg px-4 py-3 text-sm outline-none transition-all duration-200 focus:border-EBC122 focus:ring-1 focus:ring-EBC122 resize-none"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t('delivery.address.placeholder')}
                rows={4}
              />
              <p className="text-sm text-6c757d mt-2">
                {t('delivery.address.help')}
              </p>
            </div>
          </div>

        </>
      )}

      <div className="lg:hidden">
        <div className="flex justify-end">
          <button
            className="px-8 py-4 bg-primary text-2c3e50 font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSubmit}
            disabled={!deliveryType || (deliveryType === 'delivery' && !address.trim())}
          >
            {t('common.continue')}
          </button>
        </div>
      </div>
        </div>

        {/* Resumen de precios - Solo en desktop */}
        <div className="hidden lg:block lg:w-80">
          <div className="lg:sticky lg:top-4 space-y-4">
            <PriceBreakdown
              selectedVehicles={bookingData?.selectedVehicles || []}
              selectedDates={bookingData?.dates || {}}
              deliveryType={deliveryType || 'pickup'}
              selectedAccessories={bookingData?.selectedAccessories || {}}
              accessoriesByModel={accessoriesByModel}
              pricing={bookingData?.pricing}
              showInsuranceWarnings
            />
            <button
              className="w-full px-8 py-4 bg-primary text-2c3e50 font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={!deliveryType || (deliveryType === 'delivery' && !address.trim())}
            >
              {t('common.continue')}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DeliveryOptions; 